import React, { useContext, useEffect, useState } from 'react';
import { Mail, Send, Loader2, MessageSquare, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

export function HoursDigestCard() {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [nudge, setNudge] = useState(null);
  const [nudgeBusy, setNudgeBusy] = useState(false);
  const [daily, setDaily] = useState(null);
  const [dailyBusy, setDailyBusy] = useState(false);
  useEffect(() => {
    fetch(`${API_URL}/api/timesheets/weekly-hours-digest`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)).then(setCfg).catch(() => {});
    fetch(`${API_URL}/api/timesheets/hours-nudge`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)).then(setNudge).catch(() => {});
    fetch(`${API_URL}/api/staff/message-centre/schedules`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : null))
      .then((d) => setDaily((d?.jobs || []).find((j) => j.key === 'daily_timesheet') || null)).catch(() => {});
  }, []);
  const toggleDaily = async () => {
    const r = await fetch(`${API_URL}/api/staff/message-centre/schedules/daily_timesheet`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: !daily.enabled }) });
    if (r.ok) { const d = await r.json(); setDaily((d.jobs || []).find((j) => j.key === 'daily_timesheet') || daily); } else toast.error('Could not update');
  };
  const dailyMe = async () => {
    setDailyBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/timesheets/daily-email/send-me`, { method: 'POST', credentials: 'include' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Send failed');
      const fail = Object.values(d.email || {}).find((v) => String(v).startsWith('failed'));
      if (fail) toast.error(`Email failed: ${fail}`); else toast.success(`Today's timesheet breakdown emailed to you (${d.logged} staff, ${d.total_minutes} min)`);
    } catch (e) { toast.error(e.message); } finally { setDailyBusy(false); }
  };
  const toggleNudge = async () => {
    const r = await fetch(`${API_URL}/api/timesheets/hours-nudge`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: !nudge.enabled }) });
    if (r.ok) setNudge({ ...nudge, ...(await r.json()) }); else toast.error('Could not update');
  };
  const nudgeNow = async () => {
    const n = nudge.missing.filter((m) => m.has_mobile).length;
    if (!window.confirm(`Text ${n} staff member${n === 1 ? '' : 's'} who haven't logged hours this week?`)) return;
    setNudgeBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/timesheets/hours-nudge/send-now`, { method: 'POST', credentials: 'include' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Send failed');
      toast.success(`Texted ${d.texted.length}${d.already_texted.length ? ` · ${d.already_texted.length} already texted this week` : ''}${d.failed.length ? ` · ${d.failed.length} failed` : ''}`);
    } catch (e) { toast.error(e.message); } finally { setNudgeBusy(false); }
  };
  if (!cfg) return null;
  const send = async (onlyMe) => {
    if (!onlyMe && !window.confirm(`Email this week's hours to ${cfg.emails.join(', ')} now?`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/timesheets/weekly-hours-digest/send-now`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ only_me: onlyMe }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Send failed');
      const fails = Object.entries(d.email || {}).filter(([, v]) => String(v).startsWith('failed'));
      if (fails.length) toast.warning(`${fails.length} email(s) failed`, { description: fails.map(([k, v]) => `${k}: ${v}`).join(' · ') });
      else toast.success('Hours email sent');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const toggle = async () => {
    const r = await fetch(`${API_URL}/api/timesheets/weekly-hours-digest`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: !cfg.enabled, emails: cfg.emails }) });
    if (r.ok) setCfg({ ...cfg, ...(await r.json()) }); else toast.error('Could not update');
  };
  return (
    <Card className="p-4 space-y-2" data-testid="ts-hours-digest">
      {daily && (
        <div className="border-b pb-2 mb-1 space-y-1.5" data-testid="ts-daily-email">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-bold inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4" /> Daily timesheet email</div>
            <button type="button" role="switch" aria-checked={!!daily.enabled} disabled={!isAdmin} onClick={toggleDaily} data-testid="ts-daily-email-toggle"
              className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${daily.enabled ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-300'} disabled:opacity-60`}>
              {daily.enabled ? 'ON' : 'OFF'}
            </button>
          </div>
          <p className="text-xs text-gray-500" data-testid="ts-daily-email-desc">Every day at {String(daily.hour).padStart(2, '0')}:00 {(daily.recipients?.emails || []).join(', ') || 'nobody yet'} get everyone's day task by task — clock in/out, each job with its times and description, and who logged nothing. Change the time or recipients in Email &amp; Message Centre → Automated messages.</p>
          {daily.last_run_at && <p className="text-[11px] text-emerald-700">Last sent {new Date(daily.last_run_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>}
          <Button size="sm" variant="outline" onClick={dailyMe} disabled={dailyBusy} data-testid="ts-daily-email-send-me">
            {dailyBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />} Email me today's breakdown
          </Button>
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-bold inline-flex items-center gap-1.5"><Mail className="w-4 h-4" /> Friday hours email</div>
        <button type="button" role="switch" aria-checked={!!cfg.enabled} disabled={!isAdmin} onClick={toggle} data-testid="ts-hours-digest-toggle"
          className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${cfg.enabled ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-300'} disabled:opacity-60`}>
          {cfg.enabled ? 'ON' : 'OFF'}
        </button>
      </div>
      <p className="text-xs text-gray-500">Every Friday 5pm the directors ({cfg.emails.join(', ')}) get everyone's logged hours for Sat–Fri, by person, day and job type.</p>
      {cfg.last_sent_at && <p className="text-[11px] text-emerald-700">Last sent {new Date(cfg.last_sent_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>}
      {nudge && (
        <div className="border-t pt-2 mt-1 space-y-1.5" data-testid="ts-hours-nudge">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-bold inline-flex items-center gap-1.5"><MessageSquare className="w-4 h-4" /> Friday 2pm nudge text</div>
            <button type="button" role="switch" aria-checked={!!nudge.enabled} disabled={!isAdmin} onClick={toggleNudge} data-testid="ts-hours-nudge-toggle"
              className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${nudge.enabled ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-300'} disabled:opacity-60`}>
              {nudge.enabled ? 'ON' : 'OFF'}
            </button>
          </div>
          <p className="text-xs text-gray-500">Staff with nothing logged Sat–Fri get a text asking them to add their hours before 5pm.</p>
          <p className="text-xs" data-testid="ts-hours-nudge-missing">
            <span className="font-semibold">{nudge.missing.length}</span> not logged yet this week
            {nudge.missing.some((m) => !m.has_mobile) && <span className="text-amber-700"> · {nudge.missing.filter((m) => !m.has_mobile).length} without a mobile on file</span>}
          </p>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={nudgeNow} disabled={nudgeBusy || !nudge.missing.some((m) => m.has_mobile)} data-testid="ts-hours-nudge-send-now">
              {nudgeBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <MessageSquare className="w-3.5 h-3.5 mr-1" />} Text them now
            </Button>
          )}
        </div>
      )}
      {isAdmin && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => send(true)} disabled={busy} data-testid="ts-hours-digest-send-me">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />} Email me a preview</Button>
          <Button size="sm" onClick={() => send(false)} disabled={busy} className="bg-gray-900 text-white" data-testid="ts-hours-digest-send-now">Send to directors now</Button>
        </div>
      )}
    </Card>
  );
}
